/**
 * 🛡️ FALLBACK REDIRECT UTILITIES
 *
 * Centralized logic to handle "0 products found" scenarios across all pages.
 * Never let users hit dead ends - always redirect to relevant pages with products.
 *
 * 🎯 STRATEGY:
 * - Deal pages: Extract brand from deal name → Brand deals → All deals
 * - Shop pages: Use brand filter → Popular brand → Clean shop
 * - Category pages: Use brand filter → Brand category → Clean category
 * - Brand pages: Remove invalid filters → Clean brand page
 *
 * 🔧 USAGE:
 * ```typescript
 * import { handleNoProductsFallback } from '~/utils/fallback-redirects';
 *
 * if (products.length === 0 && hasFilters) {
 *   const fallbackRedirect = handleNoProductsFallback({
 *     pageType: 'deals',
 *     filters,
 *     baseFacets,
 *     url,
 *     sort,
 *     search
 *   });
 *
 *   if (fallbackRedirect) {
 *     return redirect(fallbackRedirect.url, fallbackRedirect.status);
 *   }
 * }
 * ```
 */

import type { FilterFacets } from '~/api/brand-filtering.server';
import { toBrandSlug } from '~/utils/brand-slug';

export interface FallbackOptions {
  pageType: 'deals' | 'shop' | 'category' | 'brand' | 'homepage';
  filters: Record<string, string>;
  baseFacets?: FilterFacets;
  url: URL;
  sort?: string;
  search?: string;
  categorySlug?: string; // For category pages
  brandSlug?: string; // For brand pages
  isExternalRequest?: boolean; // True for external links (Facebook ads, direct URLs, etc.)
}

export interface FallbackResult {
  url: string;
  status: number;
}

/**
 * Main fallback handler - determines the best redirect based on page type and filters
 */
export function handleNoProductsFallback(options: FallbackOptions): FallbackResult | null {
  const { pageType, filters, baseFacets, url, isExternalRequest = false } = options;

  // No fallback needed if no filters are applied
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  // 🚀 HYBRID APPROACH: Only redirect for external requests (Facebook ads, direct URLs, etc.)
  // For user-initiated filtering, return null so the UI can show a helpful message instead
  if (!isExternalRequest) {
    return null; // Let the UI handle with helpful message + suggestions
  }

  // For external requests, use smart redirects to avoid dead ends
  switch (pageType) {
    case 'deals':
      return handleDealsFallback(options);
    case 'shop':
      return handleShopFallback(options);
    case 'category':
      return handleCategoryFallback(options);
    case 'brand':
      return handleBrandFallback(options);
    case 'homepage':
      // Homepage acts like shop page for fallback purposes
      return handleShopFallback(options);
    default:
      return null;
  }
}

/**
 * Deals page fallback: Extract brand from deal name → Brand deals → All deals
 */
function handleDealsFallback(options: FallbackOptions): FallbackResult | null {
  const { filters, baseFacets, url } = options;

  // Try to extract brand from discount_applied filter
  if (filters.discount_applied && baseFacets?.brand) {
    const dealName = filters.discount_applied.toLowerCase();

    // Try to match brand from deal name against actual available brands
    const detectedBrand = baseFacets.brand.find((brandFacet) =>
      dealName.includes(brandFacet.value.toLowerCase()),
    );

    if (detectedBrand) {
      // Redirect to brand deals page using the actual brand name
      const brandSlug = createBrandSlug(detectedBrand.value);
      const redirectUrl = new URL(`/brand/${brandSlug}`, url.origin);
      redirectUrl.searchParams.set('onsale', 'true'); // Show only deals for this brand

      return {
        url: redirectUrl.toString(),
        status: 302,
      };
    }
  }

  // If no brand detected from deal name, or discount_applied with no match, redirect to all deals
  return {
    url: '/deals',
    status: 302,
  };
}

/**
 * Shop page fallback: Use brand filter → Popular brand → Clean shop
 */
function handleShopFallback(options: FallbackOptions): FallbackResult | null {
  const { filters, baseFacets, url, sort, search } = options;

  // If we have a brand filter, try to redirect to that brand page (only if it exists)
  if (filters.brand && baseFacets?.brand) {
    // Check if the brand actually exists in the available brands
    const brandExists = baseFacets.brand.some(
      (brandFacet) => brandFacet.value.toLowerCase() === filters.brand.toLowerCase(),
    );

    if (brandExists) {
      const brandSlug = createBrandSlug(filters.brand);
      const redirectUrl = new URL(`/brand/${brandSlug}`, url.origin);

      // Preserve other filters on brand page
      preserveFilters(redirectUrl, filters, ['brand'], sort, search);

      return {
        url: redirectUrl.toString(),
        status: 302,
      };
    }
  }

  // If we have other filters but no valid brand, redirect to most popular brand
  if (baseFacets?.brand && baseFacets.brand.length > 0) {
    const topBrand = baseFacets.brand[0];
    const brandSlug = createBrandSlug(topBrand.value);
    const redirectUrl = new URL(`/brand/${brandSlug}`, url.origin);

    // Preserve all current filters except invalid brand
    const validFilters = { ...filters };
    delete validFilters.brand; // Remove invalid brand filter
    preserveFilters(redirectUrl, validFilters, [], sort, search);

    return {
      url: redirectUrl.toString(),
      status: 302,
    };
  }

  // Last resort: clean shop page
  return {
    url: '/shop',
    status: 302,
  };
}

/**
 * Category page fallback: Use brand filter → Brand category → Clean category
 */
function handleCategoryFallback(options: FallbackOptions): FallbackResult | null {
  const { filters, baseFacets, url, sort, search, categorySlug } = options;

  // If we have a brand filter, redirect to that brand page with category
  if (filters.brand) {
    const brandSlug = createBrandSlug(filters.brand);
    const redirectUrl = new URL(`/brand/${brandSlug}`, url.origin);

    // Add category filter to brand page
    if (categorySlug) {
      redirectUrl.searchParams.set('category', categorySlug);
    }

    // Preserve other filters
    preserveFilters(redirectUrl, filters, ['brand'], sort, search);

    return {
      url: redirectUrl.toString(),
      status: 302,
    };
  }

  // If we have other filters, redirect to most popular brand with category
  if (baseFacets?.brand && baseFacets.brand.length > 0) {
    const topBrand = baseFacets.brand[0];
    const brandSlug = createBrandSlug(topBrand.value);
    const redirectUrl = new URL(`/brand/${brandSlug}`, url.origin);

    // Add category filter
    if (categorySlug) {
      redirectUrl.searchParams.set('category', categorySlug);
    }

    // Preserve other filters
    preserveFilters(redirectUrl, filters, [], sort, search);

    return {
      url: redirectUrl.toString(),
      status: 302,
    };
  }

  // Last resort: clean category page
  return {
    url: categorySlug ? `/category/${categorySlug}` : '/shop',
    status: 302,
  };
}

/**
 * Brand page fallback: Remove invalid filters → Clean brand page
 */
function handleBrandFallback(options: FallbackOptions): FallbackResult | null {
  const { brandSlug } = options;

  // For brand pages, just redirect to clean brand page
  return {
    url: brandSlug ? `/brand/${brandSlug}` : '/shop',
    status: 302,
  };
}

/**
 * Helper: Create SEO-friendly brand slug.
 * Delegates to the shared canonical generator — this file previously had its
 * own copy that stripped punctuation without collapsing the gaps, producing
 * URLs like /brand/papa--barkley that 404ed.
 */
const createBrandSlug = toBrandSlug;

/**
 * Helper: Preserve filters when redirecting
 */
function preserveFilters(
  redirectUrl: URL,
  filters: Record<string, string>,
  excludeKeys: string[] = [],
  sort?: string,
  search?: string,
): void {
  // Preserve filter parameters
  Object.entries(filters).forEach(([key, value]) => {
    if (!excludeKeys.includes(key) && value) {
      redirectUrl.searchParams.set(key, value);
    }
  });

  // Preserve sort and search
  if (sort && sort !== 'Sort by Popular') {
    redirectUrl.searchParams.set('sort', sort);
  }

  if (search) {
    redirectUrl.searchParams.set('search', search);
  }
}

/**
 * Helper: Check if any filters are applied
 */
export function hasActiveFilters(filters: Record<string, string>): boolean {
  return (
    Object.keys(filters).length > 0 && Object.values(filters).some((value) => value.trim() !== '')
  );
}

/**
 * Detect if this is an external request that should be redirected
 * External requests include:
 * - Direct URLs with discount_applied (Facebook ads, email campaigns)
 * - URLs with multiple filters applied simultaneously (likely external)
 * - Requests without a Referer header from the same domain
 */
export function isExternalRequest(url: URL, request?: Request): boolean {
  // Always redirect if discount_applied is present (likely from external campaigns)
  if (url.searchParams.has('discount_applied')) {
    return true;
  }

  // If we have a request object, check the referer
  if (request) {
    const referer = request.headers.get('referer');

    // No referer = likely direct access or external link
    if (!referer) {
      return true;
    }

    // Referer from different domain = external
    try {
      const refererUrl = new URL(referer);
      if (refererUrl.hostname !== url.hostname) {
        return true;
      }
    } catch {
      // Invalid referer URL = treat as external
      return true;
    }
  }

  // Multiple filters applied = likely external (users typically apply one at a time)
  const filterParams = ['brand', 'category', 'subtype', 'classification', 'size'];
  const appliedFilters = filterParams.filter((param) => url.searchParams.has(param));
  if (appliedFilters.length >= 3) {
    return true;
  }

  // Default to internal (user-initiated)
  return false;
}

/**
 * Generate smart suggestions for "no products found" UI messages
 */
export interface SmartSuggestion {
  label: string;
  url: string;
  type: 'brand' | 'category' | 'deals' | 'shop';
}

export function generateSmartSuggestions(options: FallbackOptions): SmartSuggestion[] {
  const { pageType, filters, baseFacets, url, categorySlug, brandSlug } = options;
  const suggestions: SmartSuggestion[] = [];

  // For deals pages with discount_applied
  if (pageType === 'deals' && filters.discount_applied && baseFacets?.brand) {
    const dealName = filters.discount_applied.toLowerCase();

    // Try to find a matching brand
    const detectedBrand = baseFacets.brand.find((brandFacet) =>
      dealName.includes(brandFacet.value.toLowerCase()),
    );

    if (detectedBrand) {
      const brandSlug = createBrandSlug(detectedBrand.value);
      suggestions.push({
        label: `View ${detectedBrand.value} Deals`,
        url: `/brand/${brandSlug}?onsale=true`,
        type: 'brand',
      });
    }
  }

  // For shop pages or homepage with brand filter
  if ((pageType === 'shop' || pageType === 'homepage') && filters.brand) {
    const brandSlug = createBrandSlug(filters.brand);
    suggestions.push({
      label: `View All ${filters.brand} Products`,
      url: `/brand/${brandSlug}`,
      type: 'brand',
    });
  }

  // For category pages
  if (pageType === 'category' && categorySlug) {
    suggestions.push({
      label: `View All ${categorySlug.charAt(0).toUpperCase() + categorySlug.slice(1)} Products`,
      url: `/category/${categorySlug}`,
      type: 'category',
    });
  }

  // Always suggest popular fallbacks
  suggestions.push(
    {
      label: 'View All Deals',
      url: '/deals',
      type: 'deals',
    },
    {
      label: 'Browse All Products',
      url: '/shop',
      type: 'shop',
    },
  );

  // If we have popular brands, suggest the top one
  if (baseFacets?.brand && baseFacets.brand.length > 0) {
    const topBrand = baseFacets.brand[0];
    const brandSlug = createBrandSlug(topBrand.value);

    // Only add if not already suggested
    const alreadySuggested = suggestions.some((s) => s.url.includes(`/brand/${brandSlug}`));
    if (!alreadySuggested) {
      suggestions.unshift({
        label: `Popular: ${topBrand.value}`,
        url: `/brand/${brandSlug}`,
        type: 'brand',
      });
    }
  }

  // Remove duplicates and limit to 4 suggestions
  const uniqueSuggestions = suggestions.filter(
    (suggestion, index, self) => index === self.findIndex((s) => s.url === suggestion.url),
  );

  return uniqueSuggestions.slice(0, 4);
}
